import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Get Variable List Data
 * Retrieve a simplified list of all variables for a project.
 * Returns variable IDs, keys, and descriptions for dropdown/selection purposes.
 */
export async function getVariableListData() {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/variableList`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching variable list:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get Variables List with Values
 * Retrieve a detailed list of all variables for a project with their latest values.
 * Secret values are masked with asterisks.
 */
export async function getVariablesList() {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/getVariablesList`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching variables list:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get Environment Variable List
 * Retrieve all variables for a specific environment within a project.
 * Secret values are masked with asterisks.
 */
export async function getEnvironmentVariableList({ environmentId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!environmentId) {
      return {
        status: STATUS.ERROR,
        error: "Environment ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/getEnvironmentVariableList/${environmentId}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching environment variable list:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create Variable
 * Create a new variable in a project.
 * Can be either a project-level variable or an environment-level variable.
 */
export async function createVariable({
  key,
  value = null,
  description = null,
  projectVariable = true,
  isSecret = false,
  secretManagerId = null,
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!key || key.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Variable key is required",
      };
    }

    const payload = {
      key,
      projectVariable,
      isSecret,
    };

    if (value !== null) payload.value = value;
    if (description !== null) payload.description = description;
    if (secretManagerId !== null) payload.secretManagerId = secretManagerId;

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/createVariable`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating variable:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete Variables (Bulk)
 * Delete one or more variables from a project.
 */
export async function deleteVariable({ variableIds = [] } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!variableIds || variableIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one variable ID is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/deleteVariable/bulkdelete`,
      { variableIds },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting variable:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get Variable Values
 * Retrieve values for specific variables or all variables in a project.
 * Can filter by environment and variable type. Secret values are decrypted.
 */
export async function getVariableValue({
  variableList = null,
  environmentId = null,
  type = null,
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const payload = {};

    if (variableList !== null) payload.variableList = variableList;
    if (environmentId !== null) payload.environmentId = environmentId;
    if (type !== null) payload.type = type;

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/getVariableValue`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching variable value:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update Variable Value
 * Update the value and/or description of a variable.
 * Secret values are automatically encrypted.
 */
export async function updateVariableValue({
  variable_id,
  value = null,
  description = null,
  type = null,
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!variable_id) {
      return {
        status: STATUS.ERROR,
        error: "Variable ID is required",
      };
    }

    const payload = {
      variable_id,
    };

    if (value !== null) payload.value = value;
    if (description !== null) payload.description = description;
    if (type !== null) payload.type = type;

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/updateVariableValue`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating variable value:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update Environment Variables (Bulk)
 * Update multiple environment variable values in bulk for a specific environment.
 * Secret values are automatically encrypted if isSecret is true.
 */
export async function updateEnvironmentVariable({
  environmentId,
  variableArray = [],
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!environmentId) {
      return {
        status: STATUS.ERROR,
        error: "Environment ID is required",
      };
    }

    if (!variableArray || variableArray.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one variable is required",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/variable/updateEnvironmentVariable`,
      { environmentId, variableArray },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating environment variable:", error);
    return extractErrorInfo(error);
  }
}

